// @ts-check
import React from 'react';

import Box from '@liquidweb-dev/undercurrent/Box';
import Card from '@liquidweb-dev/undercurrent/Card';
import CardContent from '@liquidweb-dev/undercurrent/CardContent';
import CardHeader from '@liquidweb-dev/undercurrent/CardHeader';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import InputAdornment from '@liquidweb-dev/undercurrent/InputAdornment';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import TextField from '@liquidweb-dev/undercurrent/TextField';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import CalendarIcon from '@liquidweb-dev/undercurrent/icons/lucide/Calendar';
import PencilIcon from '@liquidweb-dev/undercurrent/icons/lucide/Pencil';
import PayPalLogo from '@liquidweb-dev/undercurrent/logos/PayPal';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import PaymentMethodIcon from 'components/PaymentMethodIcon';
import usePaymentMethods from 'components/DialogMakePayment/usePaymentMethods';
import { Link as RouterLink } from 'react-router-dom';

/** @param {import('@liquidweb-dev/undercurrent/Box').BoxProps} props */
const PaymentBrandWrapper = ({ sx, ...rest }) => (
	<Box
		sx={combineSx(
			{
				width: '56px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				paddingX: 50,
				height: '100%',
			},
			sx,
		)}
		{...rest}
	/>
);

/**
 * @param {{
 *   paymentMethod?: string;
 *   issuer?: string;
 * }} props
 */
const PaymentBrand = ({ paymentMethod, issuer }) => {
	if (paymentMethod === 'check') {
		return (
			<PaymentBrandWrapper
				sx={({ vars }) => ({
					borderRight: `1px solid ${vars.palette.uc.border.main}`,
				})}
			>
				<PaymentMethodIcon paymentMethod="check" />
			</PaymentBrandWrapper>
		);
	}

	if (paymentMethod === 'ach') {
		return (
			<PaymentBrandWrapper
				sx={({ vars }) => ({
					borderRight: `1px solid ${vars.palette.uc.border.main}`,
				})}
			>
				<PaymentMethodIcon paymentMethod="ach" />
			</PaymentBrandWrapper>
		);
	}

	if (paymentMethod === 'paypal') {
		return (
			<PaymentBrandWrapper
				sx={({ vars }) => ({
					backgroundColor: vars.palette.uc.bg.weak,
				})}
			>
				<PaymentMethodIcon paymentMethod="paypal" />
			</PaymentBrandWrapper>
		);
	}

	switch (issuer?.toLowerCase()) {
		case 'visa':
			return (
				<PaymentBrandWrapper
					sx={({ vars }) => ({
						backgroundColor: vars.palette.uc.brand.visa.primary,
					})}
				>
					<PaymentMethodIcon
						paymentMethod="credit_card"
						issuer="visa"
						sx={({ vars, size }) => ({
							height: size.md,
							width: size.md,
							'& path': { fill: vars.palette.uc.icon.strong },
						})}
					/>
				</PaymentBrandWrapper>
			);

		case 'mastercard':
			return (
				<PaymentBrandWrapper
					sx={({ vars }) => ({
						backgroundColor: vars.palette.uc.bg.weak,
					})}
				>
					<PaymentMethodIcon
						paymentMethod="credit_card"
						issuer="mastercard"
						sx={({ size }) => ({
							height: size.md,
							width: size.md,
						})}
					/>
				</PaymentBrandWrapper>
			);

		case 'discover':
			return (
				<PaymentBrandWrapper
					sx={({ vars }) => ({
						backgroundColor: vars.palette.uc.brand.discover.primary,
					})}
				>
					<PaymentMethodIcon
						paymentMethod="credit_card"
						issuer="discover"
						sx={{
							width: '47.038px',
							height: '29.9px',
						}}
					/>
				</PaymentBrandWrapper>
			);

		case 'american_express':
			return (
				<PaymentBrandWrapper
					sx={({ vars }) => ({
						backgroundColor: vars.palette.uc.brand.americanExpress.primary,
					})}
				>
					<PaymentMethodIcon
						paymentMethod="credit_card"
						issuer="american_express"
						sx={{
							width: '53.68px',
							height: '100%',
						}}
					/>
				</PaymentBrandWrapper>
			);

		default:
			return null;
	}
};

const PaymentMethodSummary = () => {
	const {
		defaultPaymentMethod,
		instrumentListQuery: { isLoading },
	} = usePaymentMethods({ defaultOnly: true });

	const {
		expirationDate,
		issuer,
		maskedNumber,
		paymentMethod,
		processor,
		description,
	} = defaultPaymentMethod || {};

	if (isLoading) {
		return <Skeleton variant="rounded" height={42} width="100%" />;
	}

	return (
		<Box sx={{ display: 'flex', gap: 100, height: '40px' }}>
			<Box
				sx={{
					borderRadius: ({ borderRadius }) => borderRadius.roundedSm,
					border: ({ vars }) => `1px solid ${vars.palette.uc.border.main}`,
					display: 'flex',
					alignItems: 'center',
					flexGrow: 1,
					overflow: 'hidden',
				}}
			>
				<PaymentBrand
					paymentMethod={processor === 'paypal' ? 'paypal' : paymentMethod}
					issuer={issuer}
				/>

				{paymentMethod === 'check' && (
					<Box sx={{ padding: 150 }}>
						<Typography variant="label-lg">Payment by check</Typography>
					</Box>
				)}

				{paymentMethod === 'ach' && (
					<Box sx={{ padding: 150 }}>
						<Typography variant="label-lg">
							{description || `Account ending in ${maskedNumber}`}
						</Typography>
					</Box>
				)}

				{processor === 'paypal' && (
					<Box
						sx={{
							padding: 150,
							display: 'flex',
							alignItems: 'center',
							gap: 100,
						}}
					>
						<PayPalLogo sx={{ width: 48, flexShrink: 0 }} />
						<Typography variant="label-lg">connected</Typography>
					</Box>
				)}

				{(paymentMethod === 'credit_card' || paymentMethod === 'card') && (
					<Box
						sx={{
							padding: 150,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							flexGrow: 1,
						}}
					>
						<Typography variant="label-lg">
							{maskedNumber ? `•••• ${maskedNumber}` : 'Card'}
						</Typography>
						<Typography
							variant="label-lg"
							sx={{ color: ({ vars }) => vars.palette.uc.text.medium }}
						>
							Expires {expirationDate}
						</Typography>
					</Box>
				)}
			</Box>

			<IconButton
				component={RouterLink}
				to="/account/billing/payment-method"
				variant="secondary"
				size="large"
				sx={{
					flexShrink: 0,
				}}
			>
				<PencilIcon />
			</IconButton>
		</Box>
	);
};

/**
 * @typedef BuyNowPaymentMethodProps
 * @prop {{label: React.ReactNode, value: import('@liquidweb-dev/club-sauce/types/billingCycle').BillingCycleT}[]} [cycles]
 * @prop {import('@liquidweb-dev/club-sauce/types/billingCycle').BillingCycleT} [cycleValue]
 * @prop {import('@liquidweb-dev/undercurrent/TextField').TextFieldProps['onChange']} [onCycleChange]
 */

/** @param {BuyNowPaymentMethodProps & import('@liquidweb-dev/undercurrent/Card').CardProps} props */
const BuyNowPaymentMethod = ({
	cycles,
	cycleValue,
	onCycleChange,
	...cardProps
}) => {
	return (
		<Card {...cardProps}>
			<CardHeader bordered title="Payment method" />
			<CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 100 }}>
				{cycles && cycles.length > 0 && (
					<TextField
						select
						value={cycleValue}
						onChange={onCycleChange}
						fullWidth
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<CalendarIcon />
									</InputAdornment>
								),
							},
						}}
					>
						{cycles.map(({ label, value }) => (
							<MenuItem key={value} value={value}>
								{label}
							</MenuItem>
						))}
					</TextField>
				)}

				<PaymentMethodSummary />
			</CardContent>
		</Card>
	);
};

export default BuyNowPaymentMethod;
